/**
 * Reusable input component
 */

import { forwardRef } from 'react';
import './Input.css';

/**
 * Input component with label and error handling
 * @param {Object} props - Component props
 * @param {string} props.label - Input label
 * @param {string} props.type - Input type
 * @param {string} props.error - Error message
 * @param {string} props.hint - Hint text
 * @param {boolean} props.required - Whether field is required
 * @returns {React.ReactElement} Input element
 */
const Input = forwardRef(function Input({
  label,
  type = 'text',
  error,
  hint,
  required = false,
  className = '',
  id,
  ...props
}, ref) {
  const inputId = id || `input-${label?.toLowerCase().replace(/\s+/g, '-')}`;

  return (
    <div className={`input-group ${error ? 'input-group--error' : ''} ${className}`}>
      {label && (
        <label htmlFor={inputId} className="input-group__label">
          {label}
          {required && <span className="input-group__required">*</span>}
        </label>
      )}
      <input
        ref={ref}
        type={type}
        id={inputId}
        className="input-group__input"
        aria-invalid={error ? 'true' : 'false'}
        aria-describedby={error ? `${inputId}-error` : hint ? `${inputId}-hint` : undefined}
        {...props}
      />
      {hint && !error && (
        <span id={`${inputId}-hint`} className="input-group__hint">
          {hint}
        </span>
      )}
      {error && (
        <span id={`${inputId}-error`} className="input-group__error" role="alert">
          {error}
        </span>
      )}
    </div>
  );
});

export default Input;
